import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Firestore,
  PartialWithFieldValue,
  Query,
} from 'firebase/firestore'
import {
  and,
  collection,
  collectionGroup,
  doc,
  query,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { StaticModelCollection } from '../../firestore-mobx/model'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { RoomStateRubricResult } from '../../models/RoomStateRubricResult'
import type { FirestoreRoomStateRubricResult } from './schema'
import { empty, schema, writeSchema } from './schema'
import { type RubricType } from '../PromptRubricType'
import { getDocsWithError, getDocWithError } from '../../firestore-mobx/fetch'
import { type RoomStateRubricResultDetail } from '../../models/RoomStateRubricResultDetail'
import { fetchRubricPromptTestRubricResultDetailsForUser } from '../RoomStateRubricResultDetail'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreRoomStateRubricResult> = {
  toFirestore: (
    data: PartialWithFieldValue<FirestoreRoomStateRubricResult>
  ) => {
    writeSchema.partial().parse(data)

    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore,
  params: { roomId: string }
): CollectionReference<FirestoreRoomStateRubricResult> => {
  return collection(
    firestore,
    'room_state',
    params.roomId,
    'rubric_result'
  ).withConverter(converter)
}

// @ts-expect-error - unused for now
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getDocRef = (
  firestore: Firestore,
  roomId: string
): DocumentReference<FirestoreRoomStateRubricResult, DocumentData> => {
  return doc(getColRef(firestore, { roomId }), roomId)
}

function getColGroupRef(
  firestore: Firestore
): Query<FirestoreRoomStateRubricResult> {
  return collectionGroup(firestore, 'rubric_result').withConverter(converter)
}

export const buildEmptyRoomStateRubricResultCollection = (
  repository: FirebaseRepository
) => {
  return new StaticModelCollection({
    repository,
    model: RoomStateRubricResult,
    empty: empty,
  })
}

export const getRoomStateRubricResultStreamForAssignment = (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('sectionId', '==', sectionId),
      where('assignmentId', '==', assignmentId)
    )
  )

  return modelListStream(repository, q, RoomStateRubricResult)
}

export const getRoomStateRubricResultStreamForRoom = (
  repository: FirebaseRepository,
  {
    roomId,
    sectionId,
    assignmentId,
  }: { roomId: string; sectionId: string; assignmentId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('sectionId', '==', sectionId),
      where('assignmentId', '==', assignmentId),
      where('roomId', '==', roomId)
    )
  )

  return modelListStream(repository, q, RoomStateRubricResult)
}

export const getRoomStateRubricResultsForUser = (
  repository: FirebaseRepository,
  { userId, roomStateId }: { userId: string; roomStateId: string }
) => {
  const q = query(
    collection(
      repository.firestore,
      'room_state',
      roomStateId,
      'user_rubric_result',
      userId,
      'rubric_result'
    ).withConverter(converter)
  )
  return modelListStream(repository, q, RoomStateRubricResult)
}

/**
 * prompt/rubric_prompt/type/{rubricType}/prompt_version/{promptVersionId}/test/{testId}/result/{resultId}/user_ai_result docs
 *
 * returns null if no fake rubric found
 */
export const fetchRubricPromptTestRubricResultsByUserWithDetails = async (
  repository: FirebaseRepository,
  {
    rubricType,
    promptVersionId,
    testId,
  }: {
    rubricType: RubricType
    promptVersionId: string
    testId: string
  }
) => {
  // there should be one result under each test
  const resultsRef = collection(
    repository.firestore,
    'prompt',
    'rubric_prompt',
    'type',
    rubricType.toString(),
    'prompt_version',
    promptVersionId,
    'test',
    testId,
    'result'
  )

  const resultsSnap = await getDocsWithError(
    resultsRef,
    'FetchRubricPromptTestRubricResultsByUserWithDetailsError'
  )

  if (resultsSnap.empty)
    throw new Error(
      `No results found for rubric type ${rubricType}, version ${promptVersionId}, test ${testId}`
    )

  if (resultsSnap.size > 1)
    throw new Error(
      `Multiple results found for rubric type ${rubricType}, version ${promptVersionId}, test ${testId}`
    )

  const resultDoc = resultsSnap.docs[0]
  const resultId = resultDoc.id

  const ref = collection(resultDoc.ref, 'user_ai_result')

  const snapshot = await getDocsWithError(
    ref,
    'FetchRubricPromptTestRubricResultsUsersError'
  )

  // ids for for each list of results, id is one userId
  const fakeRubricResultDocs = await Promise.all(
    snapshot.docs.map((snapDoc) => {
      const ref = doc(snapDoc.ref, 'ai_result', 'fake-rubric').withConverter(
        converter
      )
      return getDocWithError(
        ref,
        'FetchRubricPromptTestRubricResultByUserError'
      )
    })
  )

  const map = new Map<
    string,
    {
      rubricResult: RoomStateRubricResult
      details: RoomStateRubricResultDetail[]
    }
  >()

  for (const docSnap of fakeRubricResultDocs) {
    if (docSnap.exists()) {
      const model = convertDocumentSnapshotToModel(
        repository,
        docSnap,
        RoomStateRubricResult
      )
      map.set(model.data.userId, { rubricResult: model, details: [] })
    }
  }

  // get all the details
  for (const [userId, value] of map) {
    const details = await fetchRubricPromptTestRubricResultDetailsForUser(
      repository,
      {
        rubricType,
        promptVersionId,
        testId,
        resultId,
        userId,
      }
    )
    map.set(userId, { rubricResult: value.rubricResult, details })
  }

  await Promise.all(
    Array.from(map.values()).map(async (val) => {
      const details = await fetchRubricPromptTestRubricResultDetailsForUser(
        repository,
        {
          rubricType,
          promptVersionId,
          testId,
          resultId,
          userId: val.rubricResult.data.userId,
        }
      )
      val.details = details
      map.set(val.rubricResult.data.userId, val)
    })
  )

  return map
}
